import { useEffect, useMemo, useState } from "react";
import { axiosInstance, buildLegacyHeaders } from "../Components/axiosFetchers.js";
import { useAuth } from "../Components/Auth/useAuth.jsx";

let cachedReferenceData = null;
let referenceDataPromise = null;

function unwrapReferencePayload(payload) {
  if (payload && typeof payload === "object" && "data" in payload) {
    return unwrapReferencePayload(payload.data);
  }

  return payload && typeof payload === "object" ? payload : {};
}

function normalizeOption(item) {
  if (item == null) {
    return null;
  }

  if (typeof item !== "object") {
    return {
      value: item,
      label: String(item),
    };
  }

  const value =
    item.value ?? item.code ?? item.key ?? item.id ?? item.slug ?? item.name ?? item.label;
  const label =
    item.label ?? item.name ?? item.display_name ?? item.title ?? String(value ?? "");

  if (value == null || String(value).trim() === "") {
    return null;
  }

  return {
    value,
    label,
  };
}

function normalizeOptionList(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map(normalizeOption).filter(Boolean);
}

function normalizeReferenceData(payload) {
  const raw = unwrapReferencePayload(payload);

  return Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [key, normalizeOptionList(value)])
  );
}

async function fetchReferenceData(user) {
  if (cachedReferenceData) {
    return cachedReferenceData;
  }

  if (!referenceDataPromise) {
    referenceDataPromise = axiosInstance
      .get(`${process.env.REACT_APP_API_URL}/reference-data`, {
        headers: buildLegacyHeaders(user),
      })
      .then((response) => {
        cachedReferenceData = normalizeReferenceData(response.data);
        return cachedReferenceData;
      })
      .finally(() => {
        referenceDataPromise = null;
      });
  }

  return referenceDataPromise;
}

function useReferenceData(keys = []) {
  const { user } = useAuth();
  const [referenceData, setReferenceData] = useState(cachedReferenceData || {});
  const [loading, setLoading] = useState(!cachedReferenceData);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    fetchReferenceData(user)
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setReferenceData(data);
        setLoading(false);
      })
      .catch((nextError) => {
        if (!isMounted) {
          return;
        }

        setError(nextError?.message || "Unable to load reference data.");
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user]);

  const selectedData = useMemo(() => {
    if (!keys.length) {
      return referenceData;
    }

    return Object.fromEntries(keys.map((key) => [key, referenceData[key] || []]));
  }, [keys, referenceData]);

  return {
    referenceData: selectedData,
    loading,
    error,
  };
}

export { useReferenceData };
